// src/pages/UserManagement.js
import React, { useState, useEffect } from "react";
import { Plus, User, Trash2, Shield, Clock, Mail, Info, X, UserPlus, Send } from "react-feather";
import UserRolesModal from "./UserRolesModal";
import { useApi } from "../hooks/useApi";
import { fetchAllUserRoles, detectHigherLevelRole } from "../services/userRolesService";
import Navigation from "../components/Navigation";
import Modal from "../components/Modal";
import InviteLinkBox from './InviteLinkBox';
import InviteUserModal from "../components/InviteUserModal";
import Button from "../components/Buttons";
import Card from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorBoundary from "../components/ErrorBoundary";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../hooks/useRBAC";

const INVITE_ROLES = [
  // OWNER is provisioning-only and cannot be invited/assigned; it surfaces as Admin.
  { value: 'ADMIN_ORG',         label: 'Admin' },
  { value: 'RISK_MANAGER_ORG',  label: 'Enterprise Risk Manager' },
  { value: 'RISK_MANAGER_SELF', label: 'Risk Manager' },
  { value: 'CONTRIBUTOR_SELF',  label: 'Contributor' },
  { value: 'APPROVER_SELF',     label: 'Approver' },
  { value: 'VIEWER_ORG',        label: 'Auditor' },
];

const emptyInviteRow = () => ({ firstName: '', lastName: '', domain: '', contactNumber: '', email: '', roleValue: 'ADMIN_ORG', status: null });

const splitRoleValue = (composite) => {
  const last = composite.lastIndexOf('_');
  return { role: composite.substring(0, last), scope: composite.substring(last + 1) };
};

const roleColors = {
  OWNER: 'bg-red-100 text-red-800',
  ADMIN: 'bg-purple-100 text-purple-800',
  ENTERPRISE_RISK_MANAGER: 'bg-indigo-100 text-indigo-800',
  RISK_MANAGER: 'bg-orange-100 text-orange-800',
  CONTRIBUTOR: 'bg-blue-100 text-blue-800',
  APPROVER: 'bg-green-100 text-green-800',
  VIEWER: 'bg-gray-100 text-gray-800',
  CUSTOM: 'bg-gray-200 text-gray-700',
};

const ROLE_NAMES_BY_VALUE = { 1: 'OWNER', 2: 'ADMIN', 3: 'RISK_MANAGER', 4: 'CONTRIBUTOR', 5: 'APPROVER', 6: 'VIEWER' };

const roleGuideEntries = [
  { name: 'Admin', role: 'ADMIN', description: 'Full organization management, user administration, settings, and all features' },
  { name: 'Enterprise Risk Manager', role: 'ENTERPRISE_RISK_MANAGER', description: 'Organization-wide assessment and risk oversight across all deals and business units' },
  { name: 'Risk Manager', role: 'RISK_MANAGER', description: 'Creates deals/business units and manages their own assessments and risks' },
  { name: 'Contributor', role: 'CONTRIBUTOR', description: 'Completes assigned assessments, updates assigned risks' },
  { name: 'Approver', role: 'APPROVER', description: 'Reviews and approves or rejects risk acceptance requests' },
  { name: 'Auditor', role: 'VIEWER', description: 'Read-only access to all dashboards, assessments, reports, and risk registers' },
];

const roleDisplayNames = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  RISK_MANAGER: 'Risk Manager',
  CONTRIBUTOR: 'Contributor',
  APPROVER: 'Approver',
  VIEWER: 'Viewer',
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [userRolesMap, setUserRolesMap] = useState({});
  const [pendingInvites, setPendingInvites] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteInviteModal, setShowDeleteInviteModal] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState(null);
  const [deletingInvite, setDeletingInvite] = useState(false);
  // Invite Team tab
  const [inviteRows, setInviteRows] = useState([emptyInviteRow()]);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteSummary, setInviteSummary] = useState(null);
  const { user, token } = useAuth();
  const { canAdministerOrganization } = useRBAC();
  const isOrgAdmin = canAdministerOrganization();
  const apiCall = useApi();
  const currentOrgId = (() => {
    try {
      const sel = localStorage.getItem('selectedOrganization');
      return sel ? JSON.parse(sel)?.organizationId : null;
    } catch { return null; }
  })();

  // ---------------- FETCH USERS AND ROLES ----------------
  const fetchUsers = async () => {
    try {
      const data = await apiCall("GET", "/users");
      setUsers(data || []);
    } catch (err) {} finally {
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const data = await apiCall("GET", "/userInvite/pending");
      setPendingInvites(data || []);
    } catch (err) {}
  };

  const fetchRoles = async () => {
    try {
      const allRoles = await fetchAllUserRoles(apiCall);
      const rolesMap = {};
      (allRoles || []).forEach(role => {
        if (!rolesMap[role.userId]) {
          rolesMap[role.userId] = {
            roles: [],
            highestRole: null,
            highestRoleValue: Infinity,
            count: 0,
            displayRole: null,
            roleColorKey: null,
          };
        }
        rolesMap[role.userId].roles.push(role);
        rolesMap[role.userId].count++;
        if (role.roleValue < rolesMap[role.userId].highestRoleValue) {
          rolesMap[role.userId].highestRole = role.role;
          rolesMap[role.userId].highestRoleValue = role.roleValue;
        }
      });
      // Compute display role per user using invite role detection
      for (const userId of Object.keys(rolesMap)) {
        const entry = rolesMap[userId];
        const { matchedRole, isAmbiguous } = detectHigherLevelRole(entry.roles, currentOrgId);
        if (matchedRole) {
          entry.displayRole = matchedRole.displayName;
          entry.roleColorKey = matchedRole.key === 'RISK_MANAGER_ORG'
            ? 'ENTERPRISE_RISK_MANAGER'
            : (ROLE_NAMES_BY_VALUE[matchedRole.role] || 'CUSTOM');
        } else if (isAmbiguous || entry.roles.length > 0) {
          entry.displayRole = 'Custom';
          entry.roleColorKey = 'CUSTOM';
        }
      }
      setUserRolesMap(rolesMap);
    } catch (err) {}
  };

  const handleDeleteInvite = async () => {
    if (!inviteToDelete) return;
    setDeletingInvite(true);
    try {
      await apiCall('DELETE', `/userInvite/${inviteToDelete.id}`);
      setShowDeleteInviteModal(false);
      setInviteToDelete(null);
      await fetchPendingInvites();
    } catch (error) {} finally {
      setDeletingInvite(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/users/${userToDelete.id}`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      await fetchUsers();
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleSendTeamInvites = async () => {
    const valid = inviteRows.filter(r => r.email.trim() && r.firstName.trim() && r.lastName.trim());
    if (!valid.length) return;
    setSendingInvites(true);
    setInviteSummary(null);
    const results = [];
    for (const row of valid) {
      const { role, scope } = splitRoleValue(row.roleValue);
      try {
        const response = await apiCall('POST', '/userInvite/invite', {
          email: row.email.trim(),
          fullName: `${row.firstName.trim()} ${row.lastName.trim()}`,
          role,
          scope,
        });
        // A response without emailDelivery (older backend, active-invite branch) counts as "sent".
        // The invite link is kept in component state only.
        results.push({
          email: row.email,
          success: true,
          emailDelivery: response?.emailDelivery === 'disabled' ? 'disabled' : 'sent',
          message: response?.message || '',
          inviteUrl: response?.inviteUrl || '',
        });
      } catch (err) {
        results.push({ email: row.email, success: false, error: err.response?.data?.message || 'Failed' });
      }
    }
    setSendingInvites(false);
    setInviteSummary(results);
    const allOk = results.every(r => r.success);
    if (allOk) {
      setInviteRows([emptyInviteRow()]);
      fetchPendingInvites();
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    if (isOrgAdmin) {
      fetchPendingInvites();
    }
  }, [token]);


  if (loading) return <LoadingSpinner />;

  const tabs = [
    { id: 'active', label: 'Active Users', icon: '👤', count: users.length },
    ...(isOrgAdmin ? [{ id: 'pending', label: 'Pending Invitations', icon: '📧', count: pendingInvites.length }] : []),
    ...(isOrgAdmin ? [{ id: 'invite', label: 'Invite Team', icon: '✉️', count: null }] : []),
  ];

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-theme-primary">
        <Navigation />

        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-content-primary">User Management</h1>
              <p className="mt-2 text-gray-600">Manage users and invitations</p>
            </div>
            {isOrgAdmin && (
              <Button onClick={() => setShowAddUserModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            )}
          </div>

          <div className="flex gap-6">
            {/* Role Guide Sidebar */}
            <div className="w-72 shrink-0">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Info className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-sm font-semibold text-content-primary">Role Guide</h2>
                </div>
                <div className="space-y-3">
                  {roleGuideEntries.map((entry) => (
                    <div key={entry.name}>
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${roleColors[entry.role]}`}>
                        {entry.name}
                      </span>
                      <p className="mt-1 text-xs text-gray-600 leading-relaxed">{entry.description}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    <span className="font-medium text-gray-600">Org scope</span> roles (Admin, Enterprise Risk Manager, Auditor) have access across the entire organization.{' '}
                    <span className="font-medium text-gray-600">Self scope</span> roles (Risk Manager, Contributor, Approver) only see items they created or are assigned to.
                  </p>
                </div>
              </div>
            </div>

          <Card className="flex-1 min-w-0">
            {/* Chrome tab bar */}
            <div className="flex items-end gap-0.5 bg-slate-100 border border-gray-200 border-b-0 rounded-t-xl px-2 pt-2 -mx-6 -mt-6 mb-0">
              {tabs.map(t => {
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                      isActive
                        ? 'bg-white text-indigo-700 border-gray-200 z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                        : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                    }`}
                    style={isActive ? { borderBottomColor: '#ffffff' } : {}}
                  >
                    <span className="text-sm leading-none">{t.icon}</span>
                    <span>{t.label}</span>
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                      isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {t.count}
                    </span>
                  </button>
                );
              })}
              <div className="flex-1 border-b border-gray-200 self-end h-[1px]" />
            </div>

            {/* Tab content */}
            <div className="pt-4">
              {/* Tab 1: Active Users */}
              {activeTab === 'active' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((u) => (
                        <tr key={u.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-content-primary">{u.fullName}</div>
                                <div className="text-sm text-gray-500">{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {userRolesMap[u.id] ? (
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                roleColors[userRolesMap[u.id].roleColorKey] || roleColors.CUSTOM
                              }`}>
                                {userRolesMap[u.id].displayRole}
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                                No roles
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedUser(u);
                                  setShowRolesModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                              >
                                <Shield className="h-4 w-4 mr-1" />
                                Edit Roles
                              </button>
                              {isOrgAdmin && (
                                <button
                                  onClick={() => {
                                    setUserToDelete(u);
                                    setShowDeleteModal(true);
                                  }}
                                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-6 py-10 text-center text-gray-500 text-sm">
                            No active users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tab 2: Pending Invitations */}
              {activeTab === 'pending' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invitee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invited On</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingInvites.map((inv) => (
                        <tr key={inv.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <Mail className="h-5 w-5 text-amber-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-content-primary">{inv.fullName}</div>
                                <div className="text-sm text-gray-500">{inv.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[inv.role] || 'bg-gray-100 text-gray-600'}`}>
                              {inv.roleDisplayName || roleDisplayNames[inv.role] || inv.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-gray-400" />
                              {new Date(inv.invitedAt).toLocaleDateString(undefined, {
                                year: 'numeric', month: 'short', day: 'numeric',
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {new Date(inv.expiresAt).toLocaleDateString(undefined, {
                              year: 'numeric', month: 'short', day: 'numeric',
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {inv.isExpired ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                Expired
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => {
                                setInviteToDelete(inv);
                                setShowDeleteInviteModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Revoke
                            </button>
                          </td>
                        </tr>
                      ))}
                      {pendingInvites.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-6 py-10 text-center text-gray-500 text-sm">
                            No pending invitations.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tab 3: Invite Team */}
              {activeTab === 'invite' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 mb-2">
                    Invite multiple team members at once. Each person will receive an email with a link to join your organization.
                  </p>

                  {inviteRows.map((row, idx) => (
                    <div key={idx} className="relative border border-gray-200 rounded-xl p-4 bg-gray-50">
                      {inviteRows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setInviteRows(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200"
                          aria-label="Remove"
                        >
                          <X size={14} />
                        </button>
                      )}

                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                        Person {idx + 1}
                      </p>

                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">First Name <span className="text-red-500">*</span></label>
                          <input
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 bg-white"
                            placeholder="First name"
                            value={row.firstName}
                            onChange={e => setInviteRows(prev => prev.map((r, i) => i === idx ? { ...r, firstName: e.target.value, status: null } : r))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Last Name <span className="text-red-500">*</span></label>
                          <input
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 bg-white"
                            placeholder="Last name"
                            value={row.lastName}
                            onChange={e => setInviteRows(prev => prev.map((r, i) => i === idx ? { ...r, lastName: e.target.value, status: null } : r))}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Domain</label>
                          <input
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 bg-white"
                            placeholder="company.com"
                            value={row.domain}
                            onChange={e => setInviteRows(prev => prev.map((r, i) => i === idx ? { ...r, domain: e.target.value } : r))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Work Email <span className="text-red-500">*</span></label>
                          <input
                            type="email"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 bg-white"
                            placeholder={row.domain ? `name@${row.domain}` : 'name@company.com'}
                            value={row.email}
                            onChange={e => setInviteRows(prev => prev.map((r, i) => i === idx ? { ...r, email: e.target.value, status: null } : r))}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
                          <input
                            type="tel"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 bg-white"
                            placeholder="+1 (555) 000-0000"
                            value={row.contactNumber}
                            onChange={e => setInviteRows(prev => prev.map((r, i) => i === idx ? { ...r, contactNumber: e.target.value } : r))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Role <span className="text-red-500">*</span></label>
                          <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 bg-white"
                            value={row.roleValue}
                            onChange={e => setInviteRows(prev => prev.map((r, i) => i === idx ? { ...r, roleValue: e.target.value } : r))}
                          >
                            {INVITE_ROLES.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                    </div>
                  ))}

                  {/* Rendered from inviteSummary, not from the rows: an all-success send resets the
                      rows, and the invite links must still be on screen afterwards. */}
                  {inviteSummary && inviteSummary.length > 0 && (
                    <div className="mb-3 rounded-lg border border-gray-200 bg-white p-3" data-testid="invite-results">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-gray-700">Invitation results</p>
                        <button
                          type="button"
                          onClick={() => setInviteSummary(null)}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Clear
                        </button>
                      </div>
                      <ul className="space-y-2">
                        {inviteSummary.map((res, i) => (
                          <li key={`${res.email}-${i}`} className="text-xs">
                            <span className="font-medium text-gray-800">{res.email}</span>{' '}
                            {!res.success ? (
                              <span className="text-red-500">{res.error}</span>
                            ) : res.emailDelivery === 'disabled' ? (
                              <>
                                <span className="text-amber-700 font-medium">Invitation created — no email sent</span>
                                {res.inviteUrl
                                  ? <InviteLinkBox url={res.inviteUrl} compact />
                                  : <p className="mt-1 text-gray-600">{res.message}</p>}
                              </>
                            ) : (
                              <span className="text-green-600 font-medium">✓ Invitation sent</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <button
                      type="button"
                      onClick={() => setInviteRows(prev => [...prev, emptyInviteRow()])}
                      className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      <UserPlus size={15} />
                      Add another person
                    </button>

                    <Button
                      onClick={handleSendTeamInvites}
                      disabled={sendingInvites || inviteRows.every(r => !r.email.trim())}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sendingInvites ? 'Sending…' : 'Send Invitations'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
          </div>
        </div>

        <InviteUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSuccess={fetchPendingInvites}
        />

        {showRolesModal && selectedUser && (
          <UserRolesModal
            isOpen={showRolesModal}
            onClose={() => {
              setShowRolesModal(false);
              setSelectedUser(null);
            }}
            user={selectedUser}
            onSaved={() => {
              fetchUsers();
              fetchRoles();
              setShowRolesModal(false);
              setSelectedUser(null);
            }}
          />
        )}

        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
          title="Delete User"
        >
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-semibold">{userToDelete?.fullName}</span>?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={showDeleteInviteModal}
          onClose={() => {
            setShowDeleteInviteModal(false);
            setInviteToDelete(null);
          }}
          title="Revoke Invitation"
        >
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to revoke the invitation for <span className="font-semibold">{inviteToDelete?.fullName}</span>?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteInviteModal(false);
                  setInviteToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={deletingInvite}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteInvite}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={deletingInvite}
              >
                {deletingInvite ? 'Revoking...' : 'Yes, Revoke'}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default UserManagement;
